import type { ReactNode } from "react";
import { FormRow, FormSection, Meter, Notice, formatDate, formatNumber } from "@omnabase/ui";
import { KEY_PROVIDER_LABELS, prettyApp, type KeyProvider } from "@omnabase/shared";
import { useCan } from "../../lib/access";
import { engineNow, keyCompany, usagePurposeLabel } from "../../lib/aiKey";
import { useFetch } from "../../lib/inbox";
import { useNav, useWs } from "../../lib/nav";
import { useSession } from "../../shell/store";

// Settings › AI › Usage (AI settings round one, 2026-10-18): this month with the server's own numbers (GET /ai/usage): the AI's drafts and how
// many a person sent, the investigations and their credits, the credits used against the limit (the kit's meter), the balance and the way to
// Billing; then what members' own AI apps did (member, app, drafts, notes, last run). For holders of `aiSettings` (the route says 403 to the rest).

type AppRow = { memberName: string; app: string | null; drafts: number; notes: number; lastRunAt: string | null };
type KeyRow = { purpose: string; /** round six: the calls' vendor */ provider?: string; calls: number; tokensIn: number; tokensOut: number; estimateEur: number };
type Usage = { month: string; drafts: number; draftsSent: number; investigations: { count: number; credits: number }; creditsUsed: number; apps: AppRow[]; /** AI settings round two: the month on the workspace's own key */ key: { provider?: KeyProvider | null; estimateEur: number; byPurpose: KeyRow[] } | null };
const n = formatNumber;
const plural = (k: number, one: string, many: string) => `${n(k)} ${k === 1 ? one : many}`;

/** `limit`: the page's line on where the month stands against the limit (the same line Capabilities shows) */
export function AiUsage({ limit }: { limit?: ReactNode }) {
  const { workspace } = useSession();
  const usage = useFetch<Usage>(`/api/v1/w/${workspace.slug}/ai/usage`);
  const u = usage.data;
  // AI settings round three (2026-10-19, decision 5): with the AI off the numbers stay, headed by one line (no date: the settings keep none)
  return <>{engineNow(workspace) === "none" && <Notice icon="info"><strong>AI is off.</strong> This month&apos;s numbers so far stay below.</Notice>}<ThisMonth usage={u} limit={limit} />{u?.key && <OnYourKey usage={u.key} />}<OwnAiApps rows={u?.apps ?? null} /></>;
}

function ThisMonth({ usage: u, limit }: { usage: Usage | null; limit?: ReactNode }) {
  const nav = useNav(); const { ws } = useWs(); const can = useCan();
  const { workspace } = useSession();
  const cap = workspace.settings.aiCreditsCapMonth;
  const used = u?.creditsUsed ?? 0;
  const billing = ws("/settings/billing");
  return (
    <FormSection title="This month" audience="admins" intro="What OmnaBase AI did this month and what it cost in credits.">
      <FormRow label="Drafts" help="Drafts the AI wrote, and how many of them a person sent.">{u ? <span>{plural(u.drafts, "draft", "drafts")} · {n(u.draftsSent)} sent</span> : null}</FormRow>
      <FormRow label="Investigations" help="Code investigations and the credits they used.">{u ? <span>{plural(u.investigations.count, "investigation", "investigations")} · {n(u.investigations.credits)} credits</span> : null}</FormRow>
      <FormRow label="Credits used" help={cap === null ? `${n(used)} credits · no limit set` : `${n(used)} of ${n(cap)} credits`}>
        <div style={{ width: 220 }}><Meter value={used} max={Math.max(cap ?? used, 1)} label="Credits used this month" /></div>
      </FormRow>
      <FormRow label="Balance" help="Credits the workspace holds; packs and the plan are under Billing.">
        <span>{n(workspace.credits.balance)} credits{can("billing") && <> · <a className="ob-link" href={billing} onClick={(e) => nav(billing, e)}>Billing</a></>}</span>
      </FormRow>
      {limit}
    </FormSection>
  );
}

/** the month on the workspace's own key (AI settings round two, 2026-10-19; brief rule 6): the estimate at the provider's list prices and the
 *  tokens in and out per kind of work, from the calls the key paid (OmnaBase's own embeddings are never in it). Round six (2026-10-21, decided
 *  after the red team's item 9): the heading's sentences name the stored key's provider, no new column; the API's rows per provider are
 *  added up per kind of work here (each call is priced at its own provider's list price; the sentence names the stored key's provider). */
const euros = (v: number) => `€${v.toFixed(2)}`;
function byPurpose(rows: KeyRow[]): KeyRow[] {
  const sum = new Map<string, KeyRow>();
  for (const r of rows) { const s = sum.get(r.purpose); sum.set(r.purpose, s ? { ...s, calls: s.calls + r.calls, tokensIn: s.tokensIn + r.tokensIn, tokensOut: s.tokensOut + r.tokensOut, estimateEur: s.estimateEur + r.estimateEur } : r); }
  return [...sum.values()];
}
function OnYourKey({ usage }: { usage: NonNullable<Usage["key"]> }) {
  const label = KEY_PROVIDER_LABELS[usage.provider ?? "anthropic"], company = keyCompany(usage.provider);
  return (
    <FormSection title="On your key" audience="admins" intro={`What the AI did on your ${label} key this month. ${company} bills it; the estimate leaves out its cache-write surcharge.`}>
      <FormRow label="Estimate" help="This month's calls on your key, at the provider's list prices."><span>about {euros(usage.estimateEur)} at {company}&apos;s list prices</span></FormRow>
      <div className="ob-dtable-card">
        <table className="ob-dtable" aria-label="Tokens by purpose">
          <thead><tr><th>Purpose</th><th>Tokens in</th><th>Tokens out</th><th className="ob-dtable__col--below-540">Calls</th><th className="ob-dtable__col--below-680">Estimate</th></tr></thead>
          <tbody>{usage.byPurpose.length === 0 ? <tr><td colSpan={5} className="ob-dtable__muted">No calls on your key yet this month.</td></tr> : byPurpose(usage.byPurpose).map((r) => (
            <tr key={r.purpose}>
              <td className="ob-dtable__grow"><span className="ob-dtable__name">{usagePurposeLabel(r.purpose)}</span></td>
              <td>{n(r.tokensIn)}</td>
              <td>{n(r.tokensOut)}</td>
              <td className="ob-dtable__col--below-540">{n(r.calls)}</td>
              <td className="ob-dtable__muted ob-dtable__col--below-680">{euros(r.estimateEur)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </FormSection>
  );
}

/** what members' own AI apps did here this month (they use no credits) */
function OwnAiApps({ rows }: { rows: AppRow[] | null }) {
  return (
    <FormSection title="Your own AI apps" audience="admins" intro="What members' own AI apps did here this month. They use no credits.">
      {rows && rows.length === 0 ? <p className="ob-form-row__help">No member&apos;s own AI worked here this month.</p> : rows && (
        <div className="ob-dtable-card">
          <table className="ob-dtable" aria-label="Your own AI apps">
            <thead><tr><th>Member</th><th>App</th><th>Drafts</th><th className="ob-dtable__col--below-540">Notes</th><th className="ob-dtable__col--below-680">Last run</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={`${r.memberName}:${r.app ?? ""}`}>
                <td className="ob-dtable__grow"><span className="ob-dtable__name">{r.memberName}</span></td>
                <td className="ob-dtable__muted">{prettyApp(r.app) || "Another app"}</td>
                <td>{n(r.drafts)}</td>
                <td className="ob-dtable__col--below-540">{n(r.notes)}</td>
                <td className="ob-dtable__muted ob-dtable__col--below-680">{r.lastRunAt ? formatDate(r.lastRunAt) : "Never"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </FormSection>
  );
}
